import React from "react";
import {
  CheckCircle2,
  Fuel,
  Settings,
  Calendar,
  Gauge,
  Bike,
  Car,
  Truck,
  Cpu,
  Zap,
  DoorOpen,
  Palette,
  Download,
  Weight,
  Layers,
  Building2,
  Leaf,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { exportVehiclePdf } from "../lib/pdfExport";
import { toast } from "sonner";

export const VehicleCard = ({ vehicle }) => {
  if (!vehicle) return null;
  const isMoto = vehicle.type_vehicule === "moto";
  const isPL = vehicle.type_vehicule === "poids_lourd";
  const Icon = isMoto ? Bike : isPL ? Truck : Car;
  const typeLabel = isMoto ? "Moto" : isPL ? "Poids lourd" : "Automobile";

  // Libellé lisible du sous-type PL
  const sousTypeLabels = {
    tracteur_solo: "Tracteur solo",
    porteur: "Porteur",
    semi_remorque: "Semi-remorque",
  };
  const sousTypeLabel = isPL ? sousTypeLabels[vehicle.sous_type_pl] : null;

  // Fiche technique détaillée (inspirée d'Ovoko)
  const specs = [
    { icon: Calendar, label: "Année", value: vehicle.annee },
    { icon: Cpu, label: "Code moteur", value: vehicle.code_moteur, mono: true },
    { icon: Settings, label: "Cylindrée", value: vehicle.cylindree },
    {
      icon: Zap,
      label: "Puissance",
      value:
        vehicle.puissance_kw && vehicle.puissance_ch
          ? `${vehicle.puissance_kw} kW · ${vehicle.puissance_ch} ch`
          : vehicle.puissance_ch
            ? `${vehicle.puissance_ch} ch`
            : "—",
    },
    { icon: Fuel, label: "Carburant", value: vehicle.carburant },
    { icon: Gauge, label: "Boîte", value: vehicle.type_boite || vehicle.boite },
    { icon: Car, label: "Carrosserie", value: vehicle.type_carrosserie },
    ...(vehicle.nombre_portes && !isMoto
      ? [{ icon: DoorOpen, label: "Portes", value: `${vehicle.nombre_portes} portes` }]
      : []),
    // Champs spécifiques Poids Lourd
    ...(isPL && vehicle.ptac_kg
      ? [{ icon: Weight, label: "PTAC", value: `${vehicle.ptac_kg.toLocaleString("fr-FR")} kg` }]
      : []),
    ...(isPL && vehicle.ptra_kg
      ? [{ icon: Weight, label: "PTRA", value: `${vehicle.ptra_kg.toLocaleString("fr-FR")} kg` }]
      : []),
    ...(isPL && vehicle.nombre_essieux
      ? [{ icon: Layers, label: "Essieux", value: `${vehicle.nombre_essieux} essieux` }]
      : []),
    ...(isPL && vehicle.type_cabine
      ? [{ icon: Building2, label: "Cabine", value: vehicle.type_cabine }]
      : []),
    ...(isPL && vehicle.norme_euro
      ? [{ icon: Leaf, label: "Norme", value: vehicle.norme_euro }]
      : []),
  ];

  return (
    <div
      data-testid="vehicle-identity-card"
      className="surface-card animate-slide-up relative overflow-hidden rounded-2xl p-6 sm:p-8"
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-amber-500/5 blur-3xl" />

      <div className="relative">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500/20 to-cyan-700/10 ring-1 ring-cyan-500/30">
              <Icon className="h-8 w-8 text-cyan-300" strokeWidth={1.8} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-400">
                  Fiche véhicule
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
                  <CheckCircle2 className="h-3 w-3" />
                  Match 100%
                </span>
              </div>
              <h2
                data-testid="vehicle-title"
                className="mt-1 font-heading text-2xl font-extrabold tracking-tight text-slate-50 sm:text-3xl"
              >
                {vehicle.marque} {vehicle.modele}
              </h2>
              <p className="text-sm text-slate-400">{vehicle.finition}</p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="rounded-md bg-slate-100 px-3 py-1.5 font-mono text-sm font-bold tracking-widest text-slate-950 shadow-md">
              {vehicle.plaque}
            </div>
            <Badge
              variant="outline"
              className="border-slate-700 bg-slate-900/50 text-xs uppercase tracking-wider text-slate-400"
            >
              {typeLabel}
            </Badge>
            {sousTypeLabel && (
              <Badge
                variant="outline"
                data-testid="vehicle-pl-soustype-badge"
                className="border-indigo-500/40 bg-indigo-500/10 text-xs uppercase tracking-wider text-indigo-300"
              >
                {sousTypeLabel}
              </Badge>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="export-vehicle-pdf"
              onClick={() => {
                try {
                  exportVehiclePdf(vehicle);
                  toast.success("Fiche véhicule téléchargée", {
                    description: "Transmets-la à ton garagiste pour éviter les erreurs de référence.",
                  });
                } catch (e) {
                  toast.error("Export PDF impossible");
                }
              }}
              className="mt-1 gap-2 border-cyan-500/40 bg-cyan-500/10 text-xs text-cyan-200 hover:bg-cyan-500 hover:text-slate-950"
            >
              <Download className="h-3.5 w-3.5" />
              Télécharger PDF
            </Button>
          </div>
        </div>

        {/* Fiche technique détaillée - inspirée Ovoko */}
        <div
          data-testid="vehicle-specs-grid"
          className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
        >
          {specs.map(({ icon: Icon, label, value, mono }) => (
            <div
              key={label}
              data-testid={`vehicle-spec-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-xl border border-slate-800/80 bg-slate-900/40 p-3"
            >
              <div className="flex items-center gap-2 text-slate-500">
                <Icon className="h-3.5 w-3.5" />
                <span className="font-mono text-[10px] uppercase tracking-widest">
                  {label}
                </span>
              </div>
              <div
                className={`mt-1 text-sm font-semibold text-slate-100 ${mono ? "font-mono text-cyan-200" : ""}`}
              >
                {value || "—"}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-slate-800/70 pt-5 text-xs text-slate-500">
          <div>
            <span className="font-mono uppercase tracking-widest">Code mine</span>{" "}
            <span className="font-mono font-semibold text-slate-300">
              {vehicle.code_mine}
            </span>
          </div>
          <div>
            <span className="font-mono uppercase tracking-widest">VIN</span>{" "}
            <span className="font-mono font-semibold text-slate-300">
              {vehicle.vin_partiel}
            </span>
          </div>
          <div>
            <span className="font-mono uppercase tracking-widest">P. fiscale</span>{" "}
            <span className="font-semibold text-slate-300">
              {vehicle.puissance_fiscale || "—"} CV
            </span>
          </div>
          {vehicle.couleur && (
            <div className="flex items-center gap-1">
              <Palette className="h-3 w-3" />
              <span className="font-semibold text-slate-300">{vehicle.couleur}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
